import {
  ProductCard,
  ProductCardGrid,
} from '@rsa-digital/evo-shared-components/components/ProductCard';
import { generateKeyForElement } from '@rsa-digital/evo-shared-components/helpers/elementKeyHelper';
import { graphql } from 'gatsby';
import React from 'react';
import {
  processImageAsset,
  processMandatoryCta,
  processOptionalCta,
  unwrapSingleton,
} from 'helpers/csTypeProcessors';
import { nonFatalBuildError } from 'helpers/errorReporting';
import { handleAndTrackLinkClick } from 'helpers/navigation';
import { CsAsset, CsCta, CsIcon } from 'types/contentStack';
const activeEnv = process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || 'development';
const isNotPreview = activeEnv !== 'preview';

type ProductCardsBlockProps = {
  heading?: string;
  intro?: string;
  html_id?: string;
  display_background: boolean;
  product_cards: {
    icon: [CsIcon] | [];
    image: CsAsset | null;
    heading: string;
    heading_link?: {
      url: string | null;
    };
    body: string | null;
    cta: [CsCta];
    link?: {
      url: string | null;
      text: string | null;
      open_in_new_tab: boolean;
    };
  }[];
};

export const query = graphql`
  fragment ProductCardsBlock on cs__product_cards {
    heading
    intro
    html_id
    display_background
    product_cards {
      icon {
        ...Icon
      }
      image {
        ...Asset
      }
      heading
      heading_link {
        url
      }
      body
      cta {
        ...Cta
      }
      link {
        url
        text
        open_in_new_tab
      }
    }
  }
`;

const ProductCardsBlock: React.FC<{
  product_cards: ProductCardsBlockProps;
}> = ({ product_cards }) => {
  const headingLinks = product_cards.product_cards.map((card) =>
    card.heading_link?.url
      ? {
          url: card.heading_link.url,
          onClick: handleAndTrackLinkClick(
            card.heading_link.url,
            'productCardHeaderLink',
            card.heading
          ),
        }
      : undefined
  );

  const links = product_cards.product_cards.map((card) => {
    // Valid if either: url and text are both present, or: url and text are both empty
    /* istanbul ignore if */
    if (
      (!!card.link && !card.link.url && !!card.link.text) ||
      (!!card.link && !!card.link.url && !card.link.text)
    ) {
      throw Error('Url and text are mandatory fields for Link!');
    }

    return card.link?.url && card.link?.text
      ? {
          url: card.link.url,
          onClick: handleAndTrackLinkClick(
            card.link.url,
            'productCardLink',
            card.link.text
          ),
          text: card.link.text,
          openInNewTab: card.link.open_in_new_tab,
        }
      : undefined;
  });
  return (
    <ProductCardGrid
      id={product_cards.html_id}
      displayBackground={product_cards.display_background}
      heading={product_cards.heading}
      intro={product_cards.intro}>
      {product_cards.product_cards.map((entry, index) => {
        const cta = isNotPreview
          ? processMandatoryCta(entry.cta, 'productCardsCta')
          : processOptionalCta(entry.cta, 'productCardsCta');
        if (!cta) {
          nonFatalBuildError(
            'CTA missing!',
            `Product Card ${index} requires a CTA in order to be displayed.`
          );
          return <></>;
        }
        return (
          <ProductCard
            key={generateKeyForElement(entry)}
            data-cy={`ProductCard${index}`}
            icon={unwrapSingleton(entry.icon)?.icon_code}
            image={processImageAsset(entry.image)}
            heading={entry.heading}
            headingLink={headingLinks[index]}
            body={entry.body ?? undefined}
            cta={cta}
            link={links[index]}
            displayBackground={product_cards.display_background}
          />
        );
      })}
    </ProductCardGrid>
  );
};

export default React.memo(ProductCardsBlock);
